/**
 * Wishlist localStorage manager + Web Components.
 */

// ── Storage ──────────────────────────────────────────────
export class Wishlist {
  static #STORAGE_KEY = 'wishlist';

  static getAll() {
    try {
      return JSON.parse(localStorage.getItem(this.#STORAGE_KEY) || '[]');
    } catch {
      return [];
    }
  }

  static has(handle) {
    return this.getAll().includes(handle);
  }

  static add(handle) {
    const items = this.getAll().filter((h) => h !== handle);
    items.unshift(handle);
    try {
      localStorage.setItem(this.#STORAGE_KEY, JSON.stringify(items));
    } catch (e) {
      console.warn('[MEG] Wishlist save failed:', e.message);
      return;
    }
    this.#dispatch();
  }

  static remove(handle) {
    const items = this.getAll().filter((h) => h !== handle);
    try {
      localStorage.setItem(this.#STORAGE_KEY, JSON.stringify(items));
    } catch (e) {
      console.warn('[MEG] Wishlist save failed:', e.message);
      return;
    }
    this.#dispatch();
  }

  static toggle(handle) {
    if (this.has(handle)) {
      this.remove(handle);
      return false;
    }
    this.add(handle);
    return true;
  }

  static count() {
    return this.getAll().length;
  }

  static #dispatch() {
    window.dispatchEvent(new CustomEvent('wishlist:changed', {
      detail: { count: this.count(), items: this.getAll() }
    }));
  }
}

// ── Wishlist Button ──────────────────────────────────────
if (!customElements.get('wishlist-button')) {
  class WishlistButton extends HTMLElement {
    #abortController;

    connectedCallback() {
      this.#abortController = new AbortController();
      const { signal } = this.#abortController;

      this.handle = this.dataset.productHandle;
      this.button = this.querySelector('button');
      this.label = this.querySelector('.wishlist-button__label');
      this.addText = this.dataset.addText || 'Add to wishlist';
      this.inText = this.dataset.inText || 'In wishlist';
      this.button.addEventListener('click', () => this.toggle(), { signal });
      this.update();
      window.addEventListener('wishlist:changed', () => this.update(), { signal });
    }

    disconnectedCallback() {
      this.#abortController?.abort();
    }

    toggle() {
      Wishlist.toggle(this.handle);
      this.update();
    }

    update() {
      const active = Wishlist.has(this.handle);
      this.classList.toggle('wishlist-button--active', active);
      this.button.setAttribute('aria-pressed', String(active));
      if (this.label) {
        this.label.textContent = active ? this.inText : this.addText;
      }
    }
  }
  customElements.define('wishlist-button', WishlistButton);
}

// ── Wishlist Header Icon ─────────────────────────────────
if (!customElements.get('wishlist-icon')) {
  class WishlistIcon extends HTMLElement {
    #observer = null;
    #observerConfig = { childList: true, subtree: true, attributes: true, attributeFilter: ['hidden'] };

    connectedCallback() {
      this.badge = this.querySelector('.wishlist-icon__badge');
      this.update();
      this._onWishlistChanged = () => this.update();
      window.addEventListener('wishlist:changed', this._onWishlistChanged);

      // Watch for section morph resetting the badge (e.g. after cart update)
      this.#observer = new MutationObserver(() => {
        this.#observer.disconnect();
        this.badge = this.querySelector('.wishlist-icon__badge');
        this.update();
        this.#observer.observe(this, this.#observerConfig);
      });
      this.#observer.observe(this, this.#observerConfig);
    }

    disconnectedCallback() {
      window.removeEventListener('wishlist:changed', this._onWishlistChanged);
      this.#observer?.disconnect();
      this.#observer = null;
    }

    update() {
      const count = Wishlist.count();
      if (this.badge) {
        this.badge.textContent = count > 0 ? count : '';
        this.badge.hidden = count === 0;
      }
    }
  }
  customElements.define('wishlist-icon', WishlistIcon);
}
